import { type MatrixClient } from 'matrix-js-sdk/src/matrix';
import { useCallback, useRef, useState } from 'react';

import { isLoginUnlockBrokenIdentity } from 'contexts/MatrixContext/loginSecretStorageUnlock';
import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import useMatrixDeviceContext from 'contexts/MatrixDeviceContext/useMatrixDeviceContext';

import {
  type AuthRedirectType,
  createResetEncryptionController,
  type ResetEncryptionController,
  type ResetKeysOptions,
} from './resetEncryptionController';
import { VerificationErrorEnum } from '../utils';

const useResetKeys = () => {
  // During login unlock the client lives on `matrixClientRef` while React context still exposes
  // `matrixClient: null` (mxLoggedIn is not yet true), so always fall back to the ref. The ref
  // is cleared on logout, so post-login it is null and `contextClient` takes over.
  const { matrixClient: contextClient, matrixClientRef } = useMatrixContext({ requireDefinedMatrixClient: false });
  const matrixClient = contextClient ?? matrixClientRef.current;
  const { setIsResettingKeys } = useMatrixDeviceContext();
  const [isResetting, setIsResetting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [authRedirectType, setAuthRedirectType] = useState<AuthRedirectType>(null);

  // Recreate the controller whenever the underlying client changes. The controller is stateful
  // (it holds the suspend/resume resolver), so it must outlive individual renders — keep it on a
  // ref keyed by the client it was built for.
  const controllerRef = useRef<ResetEncryptionController | null>(null);
  const controllerClientRef = useRef<MatrixClient | null>(null);
  if (matrixClient && controllerClientRef.current !== matrixClient) {
    controllerRef.current = createResetEncryptionController(matrixClient, setAuthRedirectType);
    controllerClientRef.current = matrixClient;
  }

  const openCrossSigningResetInMas = useCallback(async (preOpenedWindow?: Window | null): Promise<boolean> => {
    if (!controllerRef.current) return false;
    return controllerRef.current.openCrossSigningResetInMas(preOpenedWindow);
  }, []);

  // Resume a reset paused at the MAS authorisation step (Continue click).
  // Returns false when the reset has not reached the suspension point yet.
  const resumeReset = useCallback((): boolean => controllerRef.current?.resume() ?? false, []);

  const resetKeys = useCallback(
    async (options?: ResetKeysOptions): Promise<'ok' | 'auth_redirect' | 'error'> => {
      if (!controllerRef.current) return 'error';

      setIsResetting(true);
      setError(null);
      setAuthRedirectType(null);
      setIsResettingKeys(true);

      try {
        // On poisoned accounts the broken identity makes the pre-reset /keys/query settle pointless
        // (it only re-triggers NoSignatureFound and adds latency) — skip it.
        const result = await controllerRef.current.resetKeys({
          ...options,
          skipSettleDelays: options?.skipSettleDelays ?? isLoginUnlockBrokenIdentity(),
        });
        if (result === 'error') setError(VerificationErrorEnum.UNKNOWN_ERROR);
        return result;
      } finally {
        setIsResettingKeys(false);
        setIsResetting(false);
      }
    },
    [setIsResettingKeys]
  );

  return { resetKeys, resumeReset, openCrossSigningResetInMas, setIsResetting, isResetting, error, authRedirectType };
};

export default useResetKeys;
